import { getOwner } from '@ember/owner';
import { isTesting } from '@embroider/macros';
const {
  AbortController
} = window;

/**
 * @decorator
 *
 * Decorates a generator with standard FlashMessage and error handling behaviors. The resulting
 * generator is meant to be provided to Ember Concurrency @task.
 *
 * ```
 * @task
 * @apiAction('intl.string.action.error', 'intl.string.action.success')
 * *function(event) {
 *   yield doTheThing();
 * }
 * ```
 *
 * @param {string} errorToken: An ember-intl translation token for the error flash message.
 * @param {string} successToken: An ember-intl translation token for the success flash message.
 *
 *  @returns {Decorator}
 */

// The `any` here is unfortunate, but decorators in Ember are almost as big
// of a mess as generators in Typescript.
// eslint-disable-next-line @typescript-eslint/no-explicit-any

export default function apiAction(errorToken, successToken) {
  return function (_target, _name, descriptor) {
    const value = descriptor.value;
    if (!value) throw new Error('Must have a value to decorate');
    function* action(evt) {
      const fn = value?.bind(this);
      const owner = getOwner(this);
      const flashMessages = owner?.lookup('service:flash-messages');
      const intl = owner?.lookup('service:intl');
      evt?.preventDefault();
      try {
        const controller = new AbortController();
        // Controller is only ever an AbortController, but this "satisfies" the type ambiguity
        // caused by the TaskDecorator.value intersection type.
        let ret;
        if (fn) {
          ret = yield* fn(controller, evt);
        }
        if (controller.signal.aborted) return;
        if (successToken && intl) flashMessages.success(intl.t(successToken));
        return ret;
      } catch (err) {
        if (isTesting()) console.error('Error caught in API Action', err);
        let message;
        if (err.json) {
          const errJson = yield err.json();
          message = errJson.message;
        }
        if (errorToken && intl) {
          flashMessages.error(intl.t(errorToken), {
            content: message ?? err
          });
        }
      }
    }
    descriptor.value = action;
  };
}